import React, { useState, useMemo, useEffect } from 'react';
import { Row, Col, Typography, Select, Table, InputNumber, Input, Button, Tag, Space, message, Modal, Card } from 'antd';
import { CalculatorOutlined, CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { useGetStockBodega, useLiquidarRuta } from '../../../hooks/useBodegas';

const { Title, Text } = Typography;

const SeccionLiquidacion = ({ bodegas, productosDB }) => {
    const [bodegaOrigenId, setBodegaOrigenId] = useState(null);
    const [bodegaRetornoId, setBodegaRetornoId] = useState(null);
    const [inputsConteo, setInputsConteo] = useState({});
    
    // Estado para controlar el Modal de confirmación (Evita el bug del botón invisible)
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

    const liquidar = useLiquidarRuta();
    const { data: stockBodega = [], isFetching } = useGetStockBodega(bodegaOrigenId);

    const bodegasRuta = useMemo(() => bodegas.filter(b => b.tipo === 'vendedor' && b.activa), [bodegas]);
    const bodegasFisicas = useMemo(() => bodegas.filter(b => b.tipo !== 'vendedor' && b.activa), [bodegas]);

    useEffect(() => {
        const principal = bodegasFisicas.find(b => b.es_default);
        if (principal && !bodegaRetornoId) {
            setBodegaRetornoId(principal.id);
        }
    }, [bodegasFisicas]);

    const inventarioRuta = useMemo(() => {
        if (!bodegaOrigenId || stockBodega.length === 0) return [];
        return stockBodega
            .filter(item => Number(item.stock) > 0)
            .map(item => {
                const producto = productosDB.find(p => p.id === item.producto_id);
                let nombreFinal = producto?.nombre || 'Producto Desconocido';
                
                if (item.variante_id) {
                    const variante = producto?.variantes?.find(v => v.id === item.variante_id);
                    nombreFinal += ` (${variante?.atributos?.nombre || 'Var'})`;
                }

                return {
                    id_key: `${item.producto_id}_${item.variante_id || 'null'}`,
                    producto_id: item.producto_id,
                    variante_id: item.variante_id,
                    nombre: nombreFinal,
                    stock_esperado: Number(item.stock)
                };
            });
    }, [stockBodega, productosDB, bodegaOrigenId]);

    useEffect(() => { setInputsConteo({}); }, [bodegaOrigenId]);

    const handleInput = (id_key, campo, valor) => {
        setInputsConteo(prev => ({
            ...prev,
            [id_key]: { ...prev[id_key], [campo]: valor }
        }));
    };

    // Función que abre el modal si los datos están completos
    const abrirConfirmacion = () => {
        if (!bodegaOrigenId || !bodegaRetornoId) return message.error("Selecciona origen y destino.");
        if (inventarioRuta.length === 0) return message.warning("El vendedor no tiene mercancía por cuadrar");

        let totalDevuelto = 0;
        inventarioRuta.forEach(item => {
            const data = inputsConteo[item.id_key] || {};
            totalDevuelto += (data.buena !== undefined ? Number(data.buena) : 0) + (data.danada !== undefined ? Number(data.danada) : 0);
        });

        if (totalDevuelto === 0) {
            return message.warning("Debes registrar al menos un producto devuelto.");
        }

        setIsConfirmModalOpen(true);
    };

    // Función que ejecuta el POST a la BD
    const procesarLiquidacion = async () => {
        const itemsPayload = inventarioRuta.map(item => {
            const data = inputsConteo[item.id_key] || {};
            const buena = data.buena !== undefined ? Number(data.buena) : 0;
            const danada = data.danada !== undefined ? Number(data.danada) : 0;
            const faltante = item.stock_esperado - (buena + danada);

            return {
                producto_id: item.producto_id,
                variante_id: item.variante_id,
                cantidad_buena: buena,
                cantidad_danada: danada,
                cantidad_faltante: faltante > 0 ? faltante : 0,
                motivo_dano: data.motivo || ''
            };
        });

        try {
            await liquidar.mutateAsync({
                bodega_origen_id: bodegaOrigenId,
                bodega_destino_id: bodegaRetornoId,
                items: itemsPayload
            });
            message.success("Liquidación guardada exitosamente");
            setBodegaOrigenId(null); 
            setIsConfirmModalOpen(false);
        } catch (error) {
            message.error(error.message);
        }
    };

    const columnas = [
        { title: 'PRODUCTO EN CAMIÓN', dataIndex: 'nombre', width: 250, render: t => <Text strong>{t}</Text> },
        { title: 'ESPERADO', dataIndex: 'stock_esperado', width: 100, align: 'center', render: v => <Tag color="blue" style={{ fontWeight: 'bold' }}>{v}</Tag> },
        {
            title: 'BUEN ESTADO (Devuelve)', width: 150, align: 'center',
            render: (_, r) => (
                <InputNumber
                    min={0}
                    max={r.stock_esperado}
                    controls={false}
                    value={inputsConteo[r.id_key]?.buena !== undefined ? inputsConteo[r.id_key].buena : 0}
                    onChange={v => handleInput(r.id_key, 'buena', v)}
                    style={{ width: '80px', borderColor: '#52c41a' }}
                    onFocus={(e) => e.target.select()}
                />
            )
        },
        {
            title: 'DAÑADO / VENCIDO (Merma)', width: 150, align: 'center',
            render: (_, r) => (
                <InputNumber
                    min={0}
                    max={r.stock_esperado - (inputsConteo[r.id_key]?.buena || 0)}
                    controls={false}
                    value={inputsConteo[r.id_key]?.danada !== undefined ? inputsConteo[r.id_key].danada : 0}
                    onChange={v => handleInput(r.id_key, 'danada', v)}
                    style={{ width: '80px', borderColor: '#ff4d4f' }}
                    onFocus={(e) => e.target.select()}
                />
            )
        },
        {
            title: 'MOTIVO DAÑO', width: 200,
            render: (_, r) => (
                <Input
                    placeholder="Ej: Empaque roto"
                    value={inputsConteo[r.id_key]?.motivo}
                    onChange={e => handleInput(r.id_key, 'motivo', e.target.value)}
                    disabled={!inputsConteo[r.id_key]?.danada}
                />
            )
        },
        {
            title: 'FALTANTE', width: 100, align: 'center',
            render: (_, r) => {
                const buena = inputsConteo[r.id_key]?.buena || 0;
                const danada = inputsConteo[r.id_key]?.danada || 0;
                const dif = r.stock_esperado - (buena + danada);
                if (dif === r.stock_esperado) return <Text type="secondary">-</Text>;
                return <Tag color={dif > 0 ? "red" : "green"}>{dif}</Tag>;
            }
        }
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 280px)', animation: 'fadeIn 0.3s' }}>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16, flexShrink: 0, width: '100%' }}>
                <Col xs={24} md={8}>
                    <Title level={5} style={{ margin: 0 }}>Cruce de Cuentas (Liquidación)</Title>
                    <Text type="secondary" style={{ display: 'block' }}>Audita la mercancía física devuelta por los camiones.</Text>
                </Col>
                <Col xs={24} md={16} style={{ textAlign: 'right' }}>
                    <Space size="middle" wrap>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Text strong style={{fontSize: '12px'}}>🚚 Liquidar Ruta:</Text>
                            <Select
                                placeholder="Selecciona el camión..."
                                style={{ width: 180 }}
                                value={bodegaOrigenId}
                                onChange={setBodegaOrigenId}
                                options={bodegasRuta.map(b => ({ value: b.id, label: b.nombre }))}
                            />
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Text strong style={{fontSize: '12px'}}>🏢 Retornar stock a:</Text>
                            <Select
                                placeholder="Bodega destino..."
                                style={{ width: 180 }}
                                value={bodegaRetornoId}
                                onChange={setBodegaRetornoId}
                                options={bodegasFisicas.map(b => ({ value: b.id, label: b.nombre }))}
                            />
                        </div>

                        {bodegaOrigenId && inventarioRuta.length > 0 && (
                            <Button type="primary" onClick={abrirConfirmacion} icon={<CheckCircleOutlined />} style={{ background: '#22BABB', borderRadius: '8px', fontWeight: 'bold' }}>
                                Ejecutar Cuadre
                            </Button>
                        )}
                    </Space>
                </Col>
            </Row>

            {!bodegaOrigenId ? (
                <Card style={{ borderRadius: '12px', textAlign: 'center', padding: '40px 0', background: '#f8fafc', border: '1px dashed #cbd5e1' }}>
                    <CalculatorOutlined style={{ fontSize: '40px', color: '#94a3b8', marginBottom: '15px' }} />
                    <Title level={5} style={{ color: '#64748b' }}>Selecciona una Ruta de Ventas</Title>
                    <Text type="secondary">Escoge en el menú superior el camión que acaba de llegar para iniciar el conteo físico.</Text>
                </Card>
            ) : (
                <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', border: '1px solid #f0f0f0', borderRadius: '12px' }}>
                    <Table
                        dataSource={inventarioRuta}
                        loading={isFetching}
                        rowKey="id_key"
                        pagination={false}
                        size="small"
                        scroll={{ x: 900, y: 'calc(100vh - 430px)' }}
                        columns={columnas}
                        locale={{ emptyText: 'Este vendedor no tiene mercancía asignada o ya fue vaciado.' }}
                    />
                </div>
            )}

            {/* MODAL PERSONALIZADO DE CONFIRMACIÓN */}
            <Modal
                title={
                    <Space>
                        <WarningOutlined style={{ color: '#faad14', fontSize: '20px' }} />
                        <span style={{ fontSize: '18px' }}>¿Ejecutar Cuadre de Inventario?</span>
                    </Space>
                }
                open={isConfirmModalOpen}
                onCancel={() => setIsConfirmModalOpen(false)}
                footer={[
                    <Button key="back" onClick={() => setIsConfirmModalOpen(false)} style={{ borderRadius: '8px' }}>
                        Revisar de nuevo
                    </Button>,
                    <Button 
                        key="submit" 
                        type="primary" 
                        loading={liquidar.isPending} 
                        onClick={procesarLiquidacion}
                        style={{ background: '#22BABB', borderColor: '#22BABB', borderRadius: '8px', fontWeight: 'bold' }}
                    >
                        Sí, Ejecutar Cuadre
                    </Button>
                ]}
                centered
            >
                <div style={{ marginTop: '15px', marginBottom: '15px' }}>
                    <Text>
                        La mercancía en <strong>Buen Estado</strong> se enviará a la bodega seleccionada, y se registrarán las <strong>Mermas y Faltantes</strong> de forma permanente en el historial del sistema.
                    </Text>
                </div>
            </Modal>
        </div>
    );
};

export default SeccionLiquidacion;